/**
 * Every in-app path the bottom bar may link to.
 *
 * A union of the routes that actually exist rather than `string`, because a
 * `<Link>` to a path the router has never heard of is a type error — which is
 * the whole point. The sidebar sections' rows (nav-groups.ts) carry params
 * and search on top of a path, so they are typed by `linkOptions` instead,
 * which checks all three against the route tree.
 */
export type NavPath =
  | '/market'
  | '/market/movers'
  | '/market/sectors'
  | '/market/floorsheet'
  | '/market/events'
  | '/market/indices'
  | '/market/live'
  | '/market/stock-prices'
  | '/market/gainers-losers'
  | '/market/52-week'
  | '/market/depth'
  | '/market/heatmap'
  | '/market/update'
  | '/market/signals'
  | '/chart'
  | '/watchlist'
  | '/alerts'
  | '/baskets'
  | '/brokers'
  | '/brokers/compare'
  | '/mutual-funds'
  | '/screener'
  | '/screener/advanced'
  | '/portfolio'
  | '/portfolio/holdings'
  | '/portfolio/allocation'
  | '/portfolio/activity'
  | '/portfolio/income'
  | '/portfolio/analytics'
  | '/courses'
  | '/courses/path'
  | '/courses/my-learning'
  | '/learn/dictionary'
  | '/market/newsletters'

/**
 * `activeOptions` for every nav `<Link>`.
 *
 * TanStack's Link matches FUZZILY by default and stamps `aria-current="page"`
 * on any link whose path is a prefix of the current one — `/market` on
 * `/market/indices`, `/brokers` on `/brokers/all` — overriding whatever
 * `aria-current` the component passed. Every nav surface here decides
 * "current" itself (a row by its path and its own search key, a section by
 * its family), so the router's own guess is switched to exact to stop it
 * contradicting.
 */
export const EXACT_ACTIVE = { exact: true } as const

/**
 * The Shortcuts flyout's heading. The rows themselves are the member's pinned tools
 * (features/shortcuts: the catalog, and the four a member who has never opened the "+" picker
 * starts with) — this module only names the group.
 *
 * Labels there are Title Case (the owner's 2026-09-30 ruling). A shortcut lights on its own path
 * alone: My Watchlist stays lit whichever list `?list=` names.
 */
export const SHORTCUTS = {
  heading: 'Shortcuts',
} as const

/**
 * The identities the bottom bar lights (it once underlined the top nav's
 * links too, until the 2026-10-14 nav revamp took those out).
 *
 * A slot lights across its whole section. `home` is one: V2 lights Home on
 * Notifications too (`4236:77325`), so "Home" is a section of two pages
 * rather than the one exact path it used to be (What's Happening, the third,
 * is gone with its route, owner 2026-10-27). Baskets, Public Issues and
 * Mutual Funds are not sections here. The sidebar has its own, longer list
 * (`NavSectionId`, nav-groups.ts).
 */
export type SectionId = 'home' | 'market' | 'portfolio' | 'brokers' | 'learn'

/**
 * Where a page belongs, as the sidebar sees it: the section whose head lights
 * (nav-groups.ts reads a family as its `NavSectionId`).
 *
 * Finer than SectionId, on purpose. A stock page is inside Market as far as
 * the bottom bar is concerned (`4236:73517` lights Market), but its sidebar
 * section is the stock's own — headed by the ticker, listing its tabs — not
 * Market's. Public Issues is a section of its own in the sidebar and a Market
 * page in the bottom bar. So the rail and the bar answer two different
 * questions, and FAMILY_SECTION is the one place those answers are joined.
 */
export type NavFamily =
  | 'home'
  | 'market'
  | 'issues'
  | 'signals'
  | 'stock'
  | 'learn'
  | 'portfolio'
  | 'screener'
  | 'watchlist'
  | 'alerts'
  | 'account'
  | 'baskets'
  | 'brokers'
  | 'funds'
  | 'dashboards'

/**
 * Path prefix -> family. The LONGEST segment-aware match wins
 * (use-active-section.ts); order here does not matter, and a test pins that
 * no path is claimed twice. One carve-out stands today: `/market/signals`
 * claims Signal Zone's ten pages away from `/market`, so they light their own
 * section while still lighting Market (D7), and `/market/issues` does the
 * same for Public Issues. Market update was carved out the same way once, for
 * the old News & reports group, until the owner reversed that 2026-09-24 and
 * folded it back under the plain `/market` claim.
 *
 * The learn family spans two prefixes: `/courses` (the hub, a course, its
 * lectures, My learning, Certificates, the path, the FAQ) and `/learn` (the
 * dictionary). Newsletters is a Market page in the revamp mock (owner D2, 2026-10-27) and lives
 * at `/market/newsletters`; its old `/newsletters` URL only redirects there, and is claimed too so
 * the hop keeps the section lit instead of dropping it for a frame.
 *
 * `/` is matched exactly, never as a prefix — see `withinPrefix`. A route no
 * entry covers (`/companies`, the dev gallery) has no family: no section
 * lights, rather than one for somewhere else.
 *
 * `/ipo` is Public Issues': the list at `/market/issues` drills into
 * `/ipo/$key`, a URL outside `/market` (owner, 2026-10-12). `/issues`, the
 * list's old URL, only redirects there — claimed too, so the hop keeps the
 * section lit instead of dropping it for a frame.
 */
export const FAMILY_CLAIMS: readonly (readonly [prefix: string, family: NavFamily])[] = [
  ['/', 'home'],
  ['/notifications', 'home'],
  ['/market', 'market'],
  ['/market/signals', 'signals'],
  ['/courses', 'learn'],
  ['/learn', 'learn'],
  ['/newsletters', 'market'],
  ['/stocks', 'stock'],
  ['/portfolio', 'portfolio'],
  // The reader's own dashboards, a section of their own (owner, 2026-10-18).
  ['/dashboards', 'dashboards'],
  ['/screener', 'screener'],
  ['/watchlist', 'watchlist'],
  ['/alerts', 'alerts'],
  ['/profile', 'account'],
  ['/settings', 'account'],
  ['/subscription', 'account'],
  ['/payment', 'account'],
  ['/baskets', 'baskets'],
  ['/brokers', 'brokers'],
  ['/mutual-funds', 'funds'],
  ['/market/issues', 'issues'],
  ['/ipo', 'issues'],
  ['/issues', 'issues'],
]

/**
 * Which bottom-bar section each family lights. `null` lights nothing: the V2
 * frames light nothing on any Watchlist, Alerts or Account page
 * (`4236:113812`, `4236:109443`, every Account render), nor on Baskets, which
 * left the top nav in the 2026-09-28 design refresh.
 */
export const FAMILY_SECTION: Readonly<Record<NavFamily, SectionId | null>> = {
  home: 'home',
  market: 'market',
  // Public Issues is a Market page in the bottom bar (owner, 2026-10-12) and
  // a section of its own in the sidebar.
  issues: 'market',
  // Signal Zone lives under /market (owner decision D7): its pages light
  // Market in the bottom bar, while the sidebar lights its own section.
  signals: 'market',
  stock: 'market',
  learn: 'learn',
  portfolio: 'portfolio',
  // Screeners left the top nav (owner instruction, 2026-09-28): nothing
  // lights for it any more, same as Watchlist/Alerts/Account.
  screener: null,
  watchlist: null,
  alerts: null,
  account: null,
  baskets: null,
  brokers: 'brokers',
  // Mutual Funds left the top nav (owner, 2026-10-12): its pages light their
  // own sidebar section and nothing in the bottom bar.
  funds: null,
  // My Dashboards has no bottom-bar slot (five is the ceiling): the drawer's
  // sections carry it, as they carry Mutual Funds.
  dashboards: null,
}
